import { Link } from "@tanstack/react-router";
import {
  Hourglass,
  XCircle,
  CaretRight,
  EnvelopeSimple,
} from "@phosphor-icons/react";
import { formatDateTimeBs } from "@/lib/format";

interface Props {
  status: "pending" | "rejected";
  submittedAt: string;
  rejectionReason?: string | null;
}

const COPY = {
  pending: {
    heading: "Vaša prijava je u obradi",
    body: "Naš tim trenutno pregleda vaše dokumente i odgovore. Obavijestit ćemo vas putem emaila čim donesemo odluku.",
    icon: Hourglass,
    accent: "text-amber-600",
    bg: "bg-amber-50",
    timestampLabel: "Poslano",
  },
  rejected: {
    heading: "Vaša prijava nije prihvaćena",
    body: "Nažalost, vaš profil nije prošao naš proces verifikacije. Za dodatne informacije ili ponovnu prijavu kontaktirajte našu podršku.",
    icon: XCircle,
    accent: "text-rose-600",
    bg: "bg-rose-50",
    timestampLabel: "Odluka",
  },
} as const;

export function EnrollmentStatusScreen({
  status,
  submittedAt,
  rejectionReason,
}: Props) {
  const c = COPY[status];
  const Icon = c.icon;
  const formatted = formatDateTimeBs(submittedAt);

  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center px-4 py-12">
      <div className="mx-auto max-w-lg text-center">
        <div
          className={`mx-auto flex size-20 items-center justify-center rounded-2xl ${c.bg}`}
        >
          <Icon className={`size-10 ${c.accent}`} weight="duotone" />
        </div>

        <h2
          className="mt-6 font-heading text-3xl font-medium tracking-tight sm:text-4xl"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {c.heading}
        </h2>

        <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
          {c.body}
        </p>

        <p className="mt-5 text-xs text-muted-foreground">
          {c.timestampLabel}: <span className="text-foreground/80">{formatted}</span>
        </p>

        {status === "rejected" && rejectionReason && (
          <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50/60 px-4 py-3 text-left text-sm leading-relaxed text-rose-900">
            {rejectionReason}
          </div>
        )}

        <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:bg-primary/90"
          >
            Idi na početnu
            <CaretRight className="size-4" />
          </Link>
          {status === "rejected" && (
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-secondary/60"
            >
              <EnvelopeSimple className="size-4" />
              Kontaktiraj podršku
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
